import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface RecommendedUser {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  profile_picture: string;
  referral_source: 'referral' | 'invitation';
  created_at: string;
}

export const useRecommendations = () => {
  const [recommendations, setRecommendations] = useState<RecommendedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchRecommendations = async () => {
    if (!user?.id) {
      return;
    }
    
    setLoading(true);
    try {
      // Get users who referred the current user
      const { data: referrals, error: referralsError } = await supabase
        .from('referrals')
        .select(`
          referrer_id,
          created_at
        `)
        .eq('referred_id', user.id);

      let referrerProfiles = [];
      if (referrals && referrals.length > 0) {
        const referrerIds = referrals.map(r => r.referrer_id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name, profile_picture')
          .in('id', referrerIds);
        
        if (profilesError) {
          console.error('❌ Error fetching referrer profiles:', profilesError);
        } else {
          referrerProfiles = profiles || [];
        }
      }

      if (referralsError) {
        console.error('Error fetching referrals:', referralsError);
        return;
      }

      // Get processed invitations for additional context
      const { data: invitations, error: invitationsError } = await supabase
        .from('pending_profile_invitations')
        .select(`
          inviting_profile_id,
          created_at,
          profiles!inviting_profile_id(
            id,
            username,
            first_name,
            last_name,
            profile_picture
          )
        `)
        .eq('invited_email', user.email)
        .eq('is_processed', true);

      if (invitationsError) {
        console.error('Error fetching invitations:', invitationsError);
      }

      // Combine and deduplicate recommendations
      const recommendedUsers: RecommendedUser[] = [];
      const seenIds = new Set<string>();

      // Add users from referrals
      if (referrals && referrerProfiles) {
        referrals.forEach((referral) => {
          const profile = referrerProfiles.find(p => p.id === referral.referrer_id);
          
          if (profile && !seenIds.has(profile.id)) {
            seenIds.add(profile.id);
            recommendedUsers.push({
              ...profile,
              profile_picture: profile.profile_picture || '',
              referral_source: 'referral' as const,
              created_at: referral.created_at,
            });
          }
        });
      }

      // Add users from invitations
      invitations?.forEach((invitation) => {
        if (invitation.profiles && !seenIds.has(invitation.profiles.id)) {
          seenIds.add(invitation.profiles.id);
          recommendedUsers.push({
            ...invitation.profiles,
            profile_picture: invitation.profiles.profile_picture || '',
            referral_source: 'invitation' as const,
            created_at: invitation.created_at,
          });
        }
      });

      // Sort by creation date (most recent first)
      recommendedUsers.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setRecommendations(recommendedUsers);
    } catch (error) {
      console.error('Error in fetchRecommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch recommendations when user is fully authenticated
    if (user?.id && user?.email) {
      fetchRecommendations();
    }
  }, [user?.id, user?.email]);

  return {
    recommendations,
    loading,
    refetch: fetchRecommendations,
  };
};